import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WorkerSiteOwnerCompetency } from 'app/_models/worker-site-owner-competency.model';
import { Worker } from 'app/_models/worker.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs-compat';
import { catchError, map } from 'rxjs/operators';
import { BaseHttpClientService } from './base-http-client.service';
import { Pagination } from 'app/_models/pagination.model';
import { QueryStringSearch } from 'app/_models/query-string-search.model';

@Injectable()
export class WorkerService extends BaseHttpClientService {

  constructor(
    private http: HttpClient,
  ) {
    super()
  }

  public getAllUsers(email: string, firstName: string, lastName: string, phoneNumber: string, contractor: string, pagination: Pagination, sort: string[]) {
    const params = this.createQueryStrings(pagination, sort, this.buildUserSearchParams(email, firstName, lastName, phoneNumber, contractor));

    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-skylark-user', { params, observe: 'response', headers })
    .pipe(map((response) => this.handlePaginatedData(response)))
    .pipe(catchError(this.handleError));
  }

  public getAllUsersAsSiteOwnerOrDelegate(siteOwnerId: number, email: string, firstName: string, lastName: string, phoneNumber: string, contractor: string, pagination: Pagination, sort: string[]) {
    const params = this.createQueryStrings(pagination, sort, this.buildUserSearchParams(email, firstName, lastName, phoneNumber, contractor));
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-skylark-user/worker-user/by-site-owner/' + siteOwnerId, { params, observe: 'response', headers })
    .pipe(map((response) => this.handlePaginatedData(response)))
    .pipe(catchError(this.handleError));
  }

  private buildUserSearchParams(email: string, firstName: string, lastName: string, phoneNumber: string, contractor: string): QueryStringSearch[] {
    return [
      { key: 'email', value: email },
      { key: 'firstName', value: firstName },
      { key: 'lastName', value: lastName },
      { key: 'phone', value: phoneNumber },
      { key: 'contractor', value: contractor },
    ];
  }

  public getActiveSiteMembershipCount(siteOwnerSkylarkUserId: number, workerSkylarkUserId: number): Observable<number> {
    const url = environment.apiUrl
      + `api/ext-worker-user-site/site-owner/${siteOwnerSkylarkUserId}`
      + `/worker/${workerSkylarkUserId}/active-site-count`;

    return this.http.get<number>(url)
      .pipe(catchError(this.handleError));
  }

  public getWorkerById(id): Observable<Worker> {
    return this.http.get(`${environment.apiUrl}api/ext-skylark-user/${id}`)
      .pipe(map(response => this.handleData(response as HttpResponse<any>, Worker)))
      .pipe(catchError(this.handleError));
  }

  public getWorkerCompetenciesBySiteOwner(workerId, siteOwnerId, isWorker?: boolean): Observable<WorkerSiteOwnerCompetency[]> {

    const workerSegment = isWorker ? '/for-worker' : '';
    const url = environment.apiUrl +  `api/ext-worker-user-competency-site-owner/by-site-owner-skylark-id/${siteOwnerId}/for-worker-user-skylark-id/${workerId}${workerSegment}`;


    return this.http.get(url)
      .pipe(map((response) => (response as any[]).map((response) => new WorkerSiteOwnerCompetency().deserialize(response))))
      .pipe(catchError(this.handleError));
  }

  public deleteWorkerCompetencySiteOwnerById(competencyLinkId): Observable<any> {
    return this.http.delete(environment.apiUrl + `api/ext-worker-user-competency-site-owner/${competencyLinkId}`)
      .pipe(catchError(this.handleError));
  }

  public softDeleteWorkerUserBySkylarkId(skylarkUserId): Observable<any> {
    return this.http.delete(environment.apiUrl + `api/ext-skylark-user/worker-user/${skylarkUserId}`)
      .pipe(catchError(this.handleError));
  }

  public addNewRequirementToWorker(body) {
    return this.http.post(environment.apiUrl + 'api/ext-worker-user-competency-site-owner', body)
      .pipe(map((response) => (response as any[]).map((response) => new WorkerSiteOwnerCompetency().deserialize(response))))
      .pipe(catchError(this.handleError))
  }

  public getUserCount(userType: string) {
    let params = this.createHttpParams();
    params = params.append('userType', userType);
    return this.http.get(environment.apiUrl + 'api/ext-skylark-user/count', { params, observe: 'response' })
      .pipe(catchError(this.handleError))
  }

  public sendMail(body:any, attachments: any[]) : Observable<any> {
    const jsonDataMultipartName = 'email';
    const fileMultipartName = 'attachments';

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    attachments.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http.post(environment.apiUrl + 'management/send-mail', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('The attachment is infected and cannot be uploaded!');
        }
        return this.handleError(error);
      }));
  }

  public uploadCompetency(workerSiteOwnerCompetencyId, body, filesArray, isWorker?: boolean) {

    const workerSegment = isWorker ? '/for-worker' : '';
    const url = environment.apiUrl + `api/ext-worker-user-competency-site-owner/${workerSiteOwnerCompetencyId}/certificate-file${workerSegment}`;

    const jsonDataMultipartName = 'certFileUpload';
    const fileMultipartName = 'certFiles';

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    filesArray.forEach(file => {
      formData.append(fileMultipartName, file, file.name)
    });

    return this.http.post(url, formData)
      .pipe(map(response => this.handleData(response as HttpResponse<any>, WorkerSiteOwnerCompetency)))
      .pipe(catchError((error) => {
        console.log('Error', error.error);
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('Infected');
        } else if (error.error && error.error.status === 400 && error.error.errorKey === 'EmbeddedScript') {
          throw new Error('EmbeddedScript');
        }
        return this.handleError(error);
      }));
  }

  public changeContractor(skylarkUserId, newMaintenanceId) {
    let body = {
      newMaintenanceId: newMaintenanceId
    }
    return this.http.put(environment.apiUrl + `api/ext-worker-user/update-maintenance/${skylarkUserId}`, body)
      .pipe(catchError(this.handleError));
  }
}
